
import {
  Component, ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {
  Credential,
  StartCredentialRegistrationRequestOptions,
  UserNameAndPasswordCredential,
  WebAuthnPublicKeyCredential,
  WebAuthnService
} from '../../gen';
import {startRegistration} from '@simplewebauthn/browser';
import {AppConstants} from '../AppConstants';
import {ConfigurationService} from '../ui-configuration/configuration-service';
import {NgbCollapse} from '@ng-bootstrap/ng-bootstrap';
import {formatDate} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-credentials',
  templateUrl: './add-credentials.component.html',
  styleUrls: [
    './add-credentials.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AddCredentialsComponent implements OnInit, OnChanges {

  @ViewChild('passkeyCollapse') passkeyCollapse: NgbCollapse;
  @ViewChild('passwordCollapse') passwordCollapse: NgbCollapse;
  @ViewChild('passkeyInfoCollapse') passkeyInfoCollapse: NgbCollapse;
  // Angular quirks you just need to know part 347:
  // to use this add [credsReady]="callbackFunc($event)"
  @Output() credsReady = new EventEmitter();
  @Input() passwordValidation: string|RegExp;
  @Input() errors: any;
  @Input() userName: string;
  @Input() code?: string;
  @Input() displayName: string;
  form: FormGroup;
  passkeyCreds: WebAuthnPublicKeyCredential|undefined;
  passkeyActive: boolean|string;
  credentials;

  isPasskeyCollapsed = true;
  isPasskeyInfoCollapsed = true;
  isPasswordCollapsed = true;
  protected configuration: ConfigurationService;
  constructor(@Inject('WebAuthnApi') private webAuthnApi: WebAuthnService,
              private translate: TranslateService,
              configuration: ConfigurationService) {
    this.configuration = configuration;
    this.toggleCollapse = this.toggleCollapse.bind(this);
    this.togglePasskeyInfoCollapse = this.togglePasskeyInfoCollapse.bind(this);
  }

  ngOnInit() {
    const controls = {
      password: new FormControl('', [Validators.required, Validators.pattern(this.passwordValidation)]),
      'confirmPassword': new FormControl('', []),
    };
    this.validateConfirmPassword = this.validateConfirmPassword.bind(this);
    this.form = new FormGroup(controls, [Validators.required, this.validateConfirmPassword]);
    this.credentials = this.configuration.getProperties().registrationFields.filter((f) => {
      return f.key.startsWith('credentials-');
    });
    if (this.hasPassword() && this.hasPasskey()) {
      if (this.credentials?.findIndex((f) => f.key === 'credentials-' + AppConstants.AC_AM_PASSWORD) === 0) {
        this.isPasswordCollapsed = false;
      } else if (this.credentials?.findIndex((f) => f.key === 'credentials-' + AppConstants.AC_AM_WEB_AUTHN_PUBLIC_KEY) === 0) {
        this.isPasskeyCollapsed = false;
      }
    } else if (!this.hasPassword() && this.hasPasskey()) {
      this.isPasskeyCollapsed = false;
    } else if (this.hasPassword() && !this.hasPasskey()) {
      this.isPasswordCollapsed = false;
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.errors && !!this.form && !!this.form.controls) {
      Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      control.updateValueAndValidity();
    });
    }
  }
  getAppConstant(name: string): any {
    return AppConstants[name];
  }
  toggleCollapse(key: string) {
   if (key === 'passkey') {
      this.passkeyCollapse.toggle();
      if (!this.isPasswordCollapsed) {
        this.passwordCollapse.toggle();
      }
    } else if (key === 'password') {
      this.passwordCollapse.toggle();
      if (!this.isPasskeyCollapsed) {
        this.passkeyCollapse.toggle();
      }
    }
   // Always close the nested info collapse when container visibility changes
   if (!this.isPasskeyInfoCollapsed) {
     this.passkeyInfoCollapse.toggle();
   }
  }
  togglePasskeyInfoCollapse() {
    this.passkeyInfoCollapse.toggle();
  }
  hasPassword(): boolean {
    return !!this.credentials?.find((f) => f.key === 'credentials-' + AppConstants.AC_AM_PASSWORD);
  }
  hasPasskey(): boolean {
    return  !!this.credentials?.find((f) => f.key === 'credentials-' + AppConstants.AC_AM_WEB_AUTHN_PUBLIC_KEY);
  }
  isFieldInvalid(field: string): boolean {
    if (this.errors?.[field]) {
      return true;
    } else if (this.form.get(field).touched) {
      return !this.isFieldValid(field);
    }
  }

  isFieldValid(field: string): boolean {
    if (this.errors?.[field]) {
      return false;
    } else if (field === 'confirmPassword') {
      return (this.form.get(field).valid
          && (this.form.valid || !this.form.errors || !this.form.errors.pwdMatch))
        && this.form.get(field).touched;
    } else {
      return this.form.get(field).valid && this.form.get(field).touched;
    }
  }

  private validateConfirmPassword(control: FormGroup): ValidationErrors | null {
    const pwField = control.get('password');
    const cpwField = control.get('confirmPassword');
    return !cpwField || cpwField.value === pwField.value
      ? null
      : { 'pwdMatch': true };
  }
  getCredentials(): Array<UserNameAndPasswordCredential|WebAuthnPublicKeyCredential|Credential>|undefined {
    const t = [
      !this.isPasswordCollapsed && this.hasPassword() && this.form.valid ? {
        authenticationMethod: AppConstants.AC_AM_PASSWORD,
        userName: this.userName,
        password: this.form.get('password').value,
      } : undefined,
      !this.isPasskeyCollapsed && this.hasPasskey() ? this.passkeyCreds : undefined
    ].filter((f) => !!f);
    return t.length > 0 ? t : undefined;
  }
  resetPasskey() {
    this.passkeyCreds = undefined;
    this.passkeyActive = false;
  }
  startPasskeyRegister() {
    this.passkeyActive = true;
    const format = 'longDate';
    const locale = this.translate.currentLang || this.translate.defaultLang;
    const startCredsR: StartCredentialRegistrationRequestOptions = {
      name: this.userName,
      userDisplayName: this.displayName,
      credentialNickname: formatDate(new Date(), format, locale), // used for identifying the key when there are multiple.
    };
    if (this.code) {
      startCredsR.code = this.code;
    }
    this.webAuthnApi.startCredentialRegistration(startCredsR).subscribe(async (resp) => {
      this.passkeyActive = resp.requestId;
      let attResp;
      try {
        const options = JSON.parse(resp.publicKeyCredentialCreationOptions);
          // Pass the options to the authenticator and wait for a response
        attResp = await startRegistration(options);
        this.passkeyCreds = {
          authenticationMethod: AppConstants.AC_AM_WEB_AUTHN_PUBLIC_KEY,
          publicKeyCredential: JSON.stringify(attResp),
          requestId: resp.requestId,
        };
        this.passkeyActive = false;
        this.credsReady.emit(this.passkeyCreds);
      } catch (error) {
        if (this.passkeyActive === resp.requestId) {
          this.passkeyActive = false;
        }
        this.passkeyCreds = undefined;
      }
    }, (err) => {
      this.passkeyActive = false;
    });
  }
}
